import { useAuth0 } from '@auth0/auth0-react';
import { GraphQLError } from 'graphql/error';
import { useImpersonate } from '../hooks/useImpersonate';
import { removeAuthToken } from '../modules/auth/utils/storage';
import { getConfig } from '../utils/config';

const { gqlApiEndpoint } = getConfig();
const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

/**
 * Fetcher function for react-query, referenced in config for codegen.
 */
export function useFetcher<TData, TVariables>(
  query: string,
  options?: RequestInit['headers'],
): (variables?: TVariables) => Promise<TData> {
  const { getAccessTokenSilently } = useAuth0();
  const { impersonateUserToken } = useImpersonate();
  return async (variables?: TVariables): Promise<TData> => {
    const token = await getAccessTokenSilently({
      authorizationParams: {
        audience: audience,
      },
    });
    const res = await fetch(gqlApiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(impersonateUserToken && {
          'X-Impersonate': `${impersonateUserToken}`,
        }),
        ...options,
      },
      mode: 'cors',
      body: JSON.stringify({ query, variables }),
    }).catch((e) => {
      throw new GraphQLError(e);
    });

    const json = await res.json();
    const gqlError = json.errors?.[0];
    if (gqlError) {
      if (gqlError.message === 'Forbidden resource') {
        /* remove expired access token from local storage, so it can be fetched from auth0 in next network request
         as react-query retries requests by default, token will be rotated without affecting user experience */
        removeAuthToken();
      }
      throw new GraphQLError(gqlError.message, gqlError.extensions?.code, gqlError.extensions?.error);
    }

    return json.data;
  };
}
